export const perksHero = {
  title: 'Save with our global network',
  cta: { label: 'Become a Partner', href: '/partnerships' },
};

/**
 * Partner groups used by the /perks filter sidebar. Each partner belongs to
 * exactly one group; the "All" filter (added in the UI) shows every partner.
 */
export type PerkGroup =
  | 'Business & Tax'
  | 'HR & Payroll'
  | 'Software & Tools'
  | 'Education'
  | 'Travel & Insurance';

/** Display order of the group filter buttons in the sidebar. */
export const perkGroups: PerkGroup[] = [
  'Business & Tax',
  'HR & Payroll',
  'Software & Tools',
  'Education',
  'Travel & Insurance',
];

export interface Partner {
  name: string;
  description: string;
  /** Optional deal/offer or promo code shown as a badge on the card. */
  offer?: string;
  /** Partner logo under /public (real logos scraped from nsave.com/perks). */
  logo?: string;
  /** Filter group this partner belongs to (see {@link perkGroups}). */
  group: PerkGroup;
  /** Optional destination when the partner has a clear on-site page. */
  href?: string;
}
export const partners: Partner[] = [
  {
    name: 'Softileo',
    logo: '/images/perks/softileo.png',
    description:
      'A full-service software development agency that builds digital solutions to fix real business problems.',
    group: 'Software & Tools',
  },
  {
    name: 'Beyond Tahir Academy',
    logo: '/images/perks/beyond-tahir-academy.png',
    description: 'Online AI academy for professionals.',
    group: 'Education',
  },
  {
    name: 'Floot',
    logo: '/images/perks/floot.png',
    description:
      'An all-in-one platform that turns your ideas into fully-featured web apps you can monetize.',
    group: 'Software & Tools',
  },
  {
    name: 'Cercli',
    logo: '/images/perks/cercli.png',
    description: 'The modern AI-first platform for hiring, paying, and managing global teams.',
    group: 'HR & Payroll',
  },
  {
    name: 'Multiplier',
    logo: '/images/perks/multiplier.webp',
    offer: '3 months free',
    description: 'Hire, manage and pay employees and contractors across 150+ countries.',
    group: 'HR & Payroll',
  },
  {
    name: 'SafetyWing',
    logo: '/images/perks/safetywing.png',
    description: 'Remote Health is an insurance that covers remote workers across the world.',
    group: 'Travel & Insurance',
    href: '/health-insurance',
  },
  {
    name: 'Rise',
    logo: '/images/perks/rise.png',
    description:
      'Web3-native global payroll platform. Hire anyone, anywhere and pay in stablecoins or fiat across 90+ currencies.',
    group: 'HR & Payroll',
  },
  {
    name: 'HotelPlanner',
    logo: '/images/perks/hotelplanner.png',
    offer: 'Up to 30%',
    description:
      "HotelPlanner.com negotiates hotel rates for some of the world's most premier teams and events.",
    group: 'Travel & Insurance',
  },
  {
    name: 'Tax Nerd Pakistan',
    logo: '/images/perks/tax-nerd-pakistan.png',
    offer: 'NSAVE-10%',
    description:
      'A digital tax and compliance platform serving individuals and businesses in Pakistan, as well as non-resident U.S. LLC owners.',
    group: 'Business & Tax',
  },
  {
    name: 'LoudlyDev Global Solutions',
    logo: '/images/perks/loudlydev.png',
    offer: 'LDxNSAVE',
    description:
      'Web development, mobile app development, AI solutions, UI/UX design, IT staff support, and digital marketing.',
    group: 'Software & Tools',
  },
  {
    name: 'CodemanBD',
    logo: '/images/perks/codemanbd.png',
    offer: 'AIMINHAZ50',
    description: 'Global skill development and IT training.',
    group: 'Education',
  },
  {
    name: 'OFFISIO, LLC',
    logo: '/images/perks/offisio.png',
    offer: 'nSave',
    description:
      'We help non-U.S. residents form their U.S. LLCs legally, quickly, and affordably.',
    group: 'Business & Tax',
  },
  {
    name: 'Brio Inc',
    logo: '/images/perks/brio.png',
    offer: '50% off',
    description:
      'A company formation and taxation firm specializing in helping entrepreneurs establish and operate businesses in the United States and the United Kingdom.',
    group: 'Business & Tax',
  },
  {
    name: 'Notch Filing',
    logo: '/images/perks/notch-filing.png',
    offer: 'Save my $50',
    description:
      'USA and UK formation, business IDs, banking solutions, compliance and tax filing.',
    group: 'Business & Tax',
  },
  {
    name: 'Buzz Filing',
    logo: '/images/perks/buzz-filing.png',
    offer: 'NSave$35',
    description:
      'A business consulting firm that helps global entrepreneurs establish and manage U.S. companies.',
    group: 'Business & Tax',
  },
  {
    name: 'Nexus Resolution',
    logo: '/images/perks/nexus-resolution.png',
    offer: 'NEXUS1028',
    description:
      'A business services company that helps entrepreneurs start, manage, and grow their businesses.',
    group: 'Business & Tax',
  },
  {
    name: 'UpAlerts',
    logo: '/images/perks/upalerts.png',
    offer: '15% off',
    description:
      'The world\u2019s largest AI-powered job discovery platform that helps freelancers find and win work on Upwork.',
    group: 'Software & Tools',
  },
  {
    name: 'Filerify',
    logo: '/images/perks/filerify.png',
    offer: '15% off',
    description:
      'A U.S. business formation and tax compliance service provider that helps entrepreneurs worldwide start and manage their U.S. companies.',
    group: 'Business & Tax',
  },
  {
    name: 'Kuick Formation',
    logo: '/images/perks/kuick-formation.png',
    offer: 'NSAVE-KUICK15',
    description:
      'A global business formation specialist helping 5,000+ international founders set up compliant, bank-ready U.S. and UK companies.',
    group: 'Business & Tax',
  },
  {
    name: 'Be Filing',
    logo: '/images/perks/be-filing.png',
    offer: 'NSAVE20',
    description:
      'Your gateway to the US financial system: LLC formation, EIN, ITIN and tax filings — fully remote, CAA-certified.',
    group: 'Business & Tax',
  },
  {
    name: 'Scounts.pk',
    logo: '/images/perks/scounts.png',
    offer: 'nsave',
    description: 'US LLC services for non-residents: registrations and IRS filings.',
    group: 'Business & Tax',
  },
  {
    name: 'Stop Formation LLC',
    logo: '/images/perks/stop-formation.svg',
    offer: 'nSave501',
    description: 'US company formation services.',
    group: 'Business & Tax',
  },
  {
    name: 'Consultisor',
    logo: '/images/perks/consultisor.png',
    offer: '15% off',
    description:
      'Company formation, tax compliance and ongoing support for entrepreneurs in the US and UK.',
    group: 'Business & Tax',
  },
];
